import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { PublicLayout } from '~/components/layout/public-layout';
import { RoleGuard } from '~/components/layout/role-guard';
import { HomePage } from '~/features/public/home-page';
import { LoginPage } from '~/features/auth/login-page';
import { RegisterPage } from '~/features/auth/register-page';
import { LegalPage, PrivacyPage } from '~/features/public/legal-pages';
import { NotFoundPage } from '~/features/public/not-found-page';
import { BookingPage } from '~/features/booking/booking-page';
import { ClientLayout } from '~/features/client/client-layout';
import { ClientInterventionsPage } from '~/features/client/interventions-page';
import { ClientBicyclesPage } from '~/features/client/bicycles-page';
import { ClientAddressesPage } from '~/features/client/addresses-page';
import { ClientProfilePage } from '~/features/client/profile-page';
import { TechnicianAgendaPage, TechnicianLayout } from '~/features/technician/technician-pages';
import { InterventionDetailPage } from '~/features/interventions/intervention-detail-page';
import { AdminLayout } from '~/features/admin/admin-layout';
import { AdminDashboardPage } from '~/features/admin/dashboard-page';
import { AdminInterventionsPage } from '~/features/admin/interventions-page';
import { AdminPlanningPage } from '~/features/admin/planning-page';
import { AdminTemplatesPage } from '~/features/admin/templates-page';
import { AdminUsersPage } from '~/features/admin/users-page';
import { AdminCatalogPage } from '~/features/admin/catalog-page';
import { AdminCompanyPage } from '~/features/admin/company-page';
import { AdminWaitingListPage } from '~/features/admin/waiting-list-page';
import { AdminAuditPage } from '~/features/admin/audit-page';

/**
 * Les deux ecrans cartographiques embarquent Leaflet — et Geoman pour l'editeur de
 * zones : plusieurs centaines de kilo-octets qui ne concernent que l'administrateur.
 * Le chargement differe evite d'imposer ce poids au client venu prendre un
 * rendez-vous depuis son telephone.
 */
const AdminZonesPage = lazy(() =>
  import('~/features/admin/zones-page').then((module) => ({ default: module.AdminZonesPage })),
);

const AdminMapPage = lazy(() =>
  import('~/features/admin/map-page').then((module) => ({ default: module.AdminMapPage })),
);

function LazyBoundary({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex flex-1 items-center justify-center p-12" role="status">
          <Loader2 className="size-6 animate-spin text-muted-foreground" aria-hidden />
          <span className="sr-only">Chargement du module…</span>
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    children: [
      { path: '/', element: <HomePage /> },
      { path: '/reserver', element: <BookingPage /> },
      { path: '/connexion', element: <LoginPage /> },
      { path: '/inscription', element: <RegisterPage /> },
      { path: '/mentions-legales', element: <LegalPage /> },
      { path: '/donnees-personnelles', element: <PrivacyPage /> },
    ],
  },

  // --- Espace client -----------------------------------------------------------
  {
    element: <RoleGuard allow={['CLIENT', 'ADMIN', 'TECHNICIAN']} />,
    children: [
      {
        path: '/mon-compte',
        element: <ClientLayout />,
        children: [
          { index: true, element: <ClientInterventionsPage /> },
          { path: 'velos', element: <ClientBicyclesPage /> },
          { path: 'adresses', element: <ClientAddressesPage /> },
          { path: 'profil', element: <ClientProfilePage /> },
        ],
      },
    ],
  },

  // --- Espace technicien --------------------------------------------------------
  {
    element: <RoleGuard allow={['TECHNICIAN', 'ADMIN']} />,
    children: [
      {
        path: '/technicien',
        element: <TechnicianLayout />,
        children: [
          { index: true, element: <TechnicianAgendaPage /> },
          { path: 'interventions/:id', element: <InterventionDetailPage /> },
        ],
      },
    ],
  },

  // --- Back-office administrateur -------------------------------------------------
  {
    element: <RoleGuard allow={['ADMIN']} />,
    children: [
      {
        path: '/admin',
        element: <AdminLayout />,
        children: [
          { index: true, element: <AdminDashboardPage /> },
          { path: 'interventions', element: <AdminInterventionsPage /> },
          { path: 'interventions/:id', element: <InterventionDetailPage /> },
          { path: 'planning', element: <AdminPlanningPage /> },
          {
            path: 'carte',
            element: (
              <LazyBoundary>
                <AdminMapPage />
              </LazyBoundary>
            ),
          },
          {
            path: 'zones',
            element: (
              <LazyBoundary>
                <AdminZonesPage />
              </LazyBoundary>
            ),
          },
          { path: 'modeles', element: <AdminTemplatesPage /> },
          { path: 'utilisateurs', element: <AdminUsersPage /> },
          { path: 'catalogue', element: <AdminCatalogPage /> },
          { path: 'societe', element: <AdminCompanyPage /> },
          { path: 'liste-attente', element: <AdminWaitingListPage /> },
          { path: 'audit', element: <AdminAuditPage /> },
        ],
      },
    ],
  },

  { path: '/404', element: <NotFoundPage /> },
  { path: '*', element: <Navigate to="/404" replace /> },
]);
